import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Building2, 
  Plus, 
  Search, 
  Filter,
  Star,
  MapPin,
  Phone,
  Mail,
  Grid3X3,
  Table,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import hotelService, { Hotel, HotelFilters } from '../../services/hotelService';

const filterControlClasses =
  "px-3 py-2 bg-white border border-border text-sm text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors";
const toolbarButtonClasses =
  "border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors";
const hotelTypeOptions: Hotel['hotel_type'][] = ['Hotel', 'Apartment', 'Resort', 'Hostel', 'Guesthouse', 'Villa'];

const Hotels: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<HotelFilters>({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0
  });

  // Load hotels on component mount and when filters/pagination change
  useEffect(() => {
    loadHotels();
  }, [pagination.page, filters]);

  const loadHotels = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await hotelService.getHotels(
        pagination.page, 
        pagination.limit, 
        { ...filters, search: searchTerm }
      );
      
      setHotels(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        total_pages: response.pagination.total_pages
      }));
    } catch (err) {
      setError('Failed to load hotels. Please try again.');
      console.error('Error loading hotels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadHotels();
  };

const handleFilterChange = <K extends keyof HotelFilters>(
  key: K,
  value: HotelFilters[K] | '' | null | undefined
) => {
  setFilters(prev => {
    const updated: HotelFilters = { ...prev };
    const normalizedValue =
      value === undefined ||
      value === null ||
      value === '' ||
      (typeof value === 'number' && Number.isNaN(value))
        ? undefined
        : value;

    if (normalizedValue === undefined) {
      delete updated[key];
    } else {
      updated[key] = normalizedValue;
    }

    return updated;
  });
  setPagination(prev => ({ ...prev, page: 1 }));
};

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-emerald-700 bg-emerald-100';
      case 'inactive':
        return 'text-red-600 bg-red-100';
      case 'suspended':
        return 'text-amber-700 bg-amber-100';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-amber-400 fill-current' : 'text-muted-foreground opacity-40'}`}
      />
    ));
  };

  const renderGridView = () => (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {hotels.map((hotel) => (
        <Card key={hotel.id} className="hover:shadow-md transition-shadow">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">
                  {hotel.hotel_name}
                  {hotel.extranet_hotel_id && (
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      ({hotel.extranet_hotel_id})
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="mt-1 flex items-center space-x-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {hotel.city}, {hotel.country}
                  </span>
                </CardDescription>
              </div>
              <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(hotel.status)}`}>
                {hotel.status}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {hotel.star_rating && (
              <div className="flex items-center space-x-1 text-muted-foreground">
                {renderStars(hotel.star_rating)}
                <span className="ml-2 text-sm">
                  ({hotel.star_rating} {hotel.star_rating === 1 ? 'star' : 'stars'})
                </span>
              </div>
            )}

            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4" />
                <span>{hotel.hotel_type}</span>
              </div>
              {hotel.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>{hotel.phone}</span>
                </div>
              )}
              {hotel.reservation_email && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>{hotel.reservation_email}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-border pt-4">
              <span className="text-sm text-muted-foreground">{hotel.destination}</span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className={toolbarButtonClasses}
                  onClick={() => navigate(`/hotels/${hotel.id}/edit`)}
                >
                  Edit
                </Button>
                <Button size="sm" onClick={() => navigate(`/hotels/${hotel.id}`)}>
                  View
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderTableView = () => (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="py-3 px-4">Hotel Name</th>
                <th className="py-3 px-4">Location</th>
                <th className="py-3 px-4">Rating</th>
                <th className="py-3 px-4">Type</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Destination</th>
                <th className="py-3 px-4">Contact</th>
                <th className="py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {hotels.map((hotel) => (
                <tr key={hotel.id} className="border-b border-border transition-colors hover:bg-muted/80">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium text-foreground">{hotel.hotel_name}</p>
                      {hotel.extranet_hotel_id && (
                        <p className="text-xs text-muted-foreground">{hotel.extranet_hotel_id}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span className="text-foreground">
                        {hotel.city}, {hotel.country}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-muted-foreground">
                    {hotel.star_rating ? (
                      <div className="flex items-center space-x-1">
                        {renderStars(hotel.star_rating)}
                        <span className="ml-1 text-sm text-muted-foreground">
                          ({hotel.star_rating})
                        </span>
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-foreground">{hotel.hotel_type}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(hotel.status)}`}>
                      {hotel.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-foreground">{hotel.destination}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="space-y-1 text-sm text-muted-foreground">
                      {hotel.phone && (
                        <div className="flex items-center space-x-2">
                          <Phone className="h-3 w-3" />
                          <span>{hotel.phone}</span>
                        </div>
                      )}
                      {hotel.reservation_email && (
                        <div className="flex items-center space-x-2">
                          <Mail className="h-3 w-3" />
                          <span>{hotel.reservation_email}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className={toolbarButtonClasses}
                        onClick={() => navigate(`/hotels/${hotel.id}/edit`)}
                      >
                        Edit
                      </Button>
                      <Button size="sm" onClick={() => navigate(`/hotels/${hotel.id}`)}>
                        View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null;

    return (
      <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-white px-4 py-3 shadow-sm">
        <div className="text-sm text-muted-foreground">
          Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} hotels
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className={`${toolbarButtonClasses} disabled:opacity-50`}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          <span className="px-3 text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.total_pages}
            className={`${toolbarButtonClasses} disabled:opacity-50`}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (loading && hotels.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Hotels Management</h1>
            <p className="text-muted-foreground">Manage hotel inventory and properties</p>
          </div>
          <Button 
            onClick={() => navigate('/hotels/new')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Hotel
          </Button>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span>Loading hotels...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Hotels Management</h1>
          <p className="text-muted-foreground">Manage hotel inventory and properties</p>
        </div>
        <Button onClick={() => navigate('/hotels/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Add Hotel
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
              <Input
                placeholder="Search hotels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              className={filterControlClasses}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            <select
              value={filters.star_rating ?? ''}
              onChange={(e) => {
                const selectedValue = e.target.value;
                handleFilterChange('star_rating', selectedValue ? parseInt(selectedValue, 10) : undefined);
              }}
              className={filterControlClasses}
            >
              <option value="">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
            <div className="flex overflow-hidden rounded-md border border-border">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={`rounded-none ${viewMode === 'grid' ? '' : 'text-muted-foreground hover:bg-secondary'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className={`rounded-none ${viewMode === 'table' ? '' : 'text-muted-foreground hover:bg-secondary'}`}
              >
                <Table className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Input
              placeholder="City"
              value={filters.city ?? ''}
              onChange={(e) => handleFilterChange('city', e.target.value)}
              className={filterControlClasses}
            />
            <Input
              placeholder="Country"
              value={filters.country ?? ''}
              onChange={(e) => handleFilterChange('country', e.target.value)}
              className={filterControlClasses}
            />
            <select
              value={filters.hotel_type || ''}
              onChange={(e) =>
                handleFilterChange(
                  'hotel_type',
                  e.target.value ? (e.target.value as Hotel['hotel_type']) : undefined
                )
              }
              className={filterControlClasses}
            >
              <option value="">All Types</option>
              {hotelTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <Input
              placeholder="Extranet Hotel ID"
              value={filters.extranet_hotel_id ?? ''}
              onChange={(e) => handleFilterChange('extranet_hotel_id', e.target.value)}
              className={filterControlClasses}
            />
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span>{error}</span>
        </div>
      )}

      {/* Hotels Content */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <span>Loading...</span>
          </div>
        </div>
      ) : hotels.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-medium text-foreground">No hotels found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? renderGridView() : renderTableView()}
          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default Hotels;
